// Vocabulary flash-card deck (voc- prefix) — Playful Frost glass, real 3D flip.
//
// LAYOUT. Progress line, then the card with an arrow either side of it, then
// the one decision the card asks for ("I know this") as a quiet link below.
// No tabs, no chips, no session counters — everything that is not the card is
// chrome competing with the card, and the learner came for the card.
//
// THE TWO SIDES ARE DIFFERENT SURFACES, NOT ONE SURFACE TWICE. English is the
// light frosted face; Turkish is warm. That is not decoration: a learner
// glancing at a card that turns has to know which language they are looking at
// before they read a word of it, and on a card like this, ground colour says
// that faster than any label can. Every tinted element follows the same shift,
// so each side reads whole.
//
// THE WARM RAMP IS THE APP'S OWN. #FCE0D2 → #F8BFA4 → #F2865F with ink #B23A1C
// is the "Ortalama puan" stat tile's gradient on the learning path. The card
// takes its SOFT END only: a tile is a 44px disc where the saturated stop
// reads as an accent, while a 500px card filled with it reads as a warning.
// Keeping the ramp (rather than inventing a pastel) is what makes the two
// surfaces look like the same product.
//
// And the warm face carries DARK type. White on any of these stops is under
// 3:1 — unreadable for a gloss or an example sentence. On #F8BFA4 the app's
// darkest ink is 10:1 and the ramp's own #B23A1C is 3.7:1, which is why the
// headword may use the brand ink but the body text may not.
//
// COLOURS AND GLASS COME FROM THE APP, not from this file: the faces use
// --glass-popover-bg-strong / --glass-popover-filter, the same measure the
// modal shell itself is built from, so the card belongs to the surface it sits
// on. The deck paints NO panel of its own — the modal is already a glass panel,
// and a second one inside it just reads as two stacked cards.
//
// THE FLIP. A genuine rotateY on a preserve-3d stage, not a content swap: the
// card has a FRONT and a BACK occupying the same space, each hidden from the
// other by backface-visibility. That is why both faces are absolutely
// positioned inside .voc-flip and the stage carries the height — two
// absolutely-positioned faces have no height of their own. The back face is
// pre-rotated 180° so it lands upright once the stage turns; flipping the
// stage alone would show the Turkish side mirrored.
//
// backdrop-filter + a 3D flip is the one combination that needs care: a
// filtered element is its own stacking context, and some engines stop
// honouring backface-visibility on it, which would show the Turkish side
// through the front. The faces therefore ALSO swap opacity at the midpoint of
// the turn, where the card is edge-on and the swap cannot be seen. Belt and
// braces, invisible when backface-visibility works.
export const vocabularyCardDeckStyles = `
  /* One column normally, two when the known-word list is open. The deck does
     NOT shrink away when it opens: the learner is comparing a list of words
     they cleared against the cards still in front of them, so hiding the
     cards to show the list would defeat the comparison. */
  .voc-deck {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: var(--space-4);
  }
  /* Capped, not greedy. The card column takes a card's worth of width and no
     more, so opening the side list spends the room the shell already had
     instead of squeezing the card — and so the card does not stretch into a
     banner on a wide shell when the list is closed. */
  .voc-col {
    flex: 1;
    min-width: 0;
    max-width: 760px;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  /* The deck's own size comes from its container, and in a modal that is the
     shell (900px). On a full-width page there is nothing to stop it, and a
     500px card stretched across a desktop content column stops reading as a
     card at all. This gives the page the same measure the modal does. */
  .voc-page {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
  }

  /* Progress first, above the card: it is what a learner wants before they
     commit to a card, not a footnote after it. */
  .voc-track {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }
  /* The sub-group heading a host may put over the line, with the count
     moved up beside it so the bar can run the full width under both. */
  .voc-track-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
    margin-bottom: calc(var(--space-2) - var(--space-4));
  }
  .voc-track-label {
    margin: 0;
    min-width: 0;
    font-family: var(--font-sans);
    font-size: var(--text-xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    overflow-wrap: anywhere;
  }
  .voc-track-label::first-letter { text-transform: uppercase; }
  .voc-progress-bar {
    flex: 1;
    height: 8px;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue-50);
    overflow: hidden;
  }
  .voc-progress-fill {
    height: 100%;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue);
    transition: width var(--duration-slow) var(--ease-glass);
  }
  .voc-count {
    flex: none;
    font-family: var(--font-sans);
    font-size: var(--text-base);
    font-weight: var(--font-weight-bold);
    color: var(--catchup-blue-700);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  /* The card between its two arrows. The arrows are fixed-width and the stage
     takes the rest, so a thumb reaching for "next" never lands on the flip
     target and the card's width is the same on every card. */
  .voc-rail {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }
  .voc-arrow {
    flex: none;
    width: 52px;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--glass-border);
    border-radius: var(--rad-catchup-full);
    background: var(--glass-flat-solid);
    box-shadow: var(--glass-flat-float-rest);
    color: var(--catchup-blue-600);
    cursor: pointer;
    transition:
      box-shadow var(--duration-base) var(--ease-standard),
      transform var(--duration-fast) var(--ease-standard),
      opacity var(--duration-base) var(--ease-standard);
  }
  .voc-arrow:hover:not(:disabled) {
    box-shadow: var(--glass-flat-float-hover);
    transform: scale(1.06);
  }
  .voc-arrow:focus-visible {
    outline: 2px solid var(--catchup-blue);
    outline-offset: 2px;
  }
  /* Dimmed rather than removed: the rail must not reflow when the learner
     reaches either end of the deck. */
  .voc-arrow:disabled {
    color: var(--catchup-light-gray);
    box-shadow: none;
    opacity: 0.5;
    cursor: not-allowed;
  }

  .voc-stage {
    flex: 1;
    min-width: 0;
    perspective: 1600px;
  }
  /* 500px is the budget for the WORST card, not the average one: a headword
     like "not to bring food or drink into the classroom" wraps to two lines at
     26px, and the face clips rather than scrolls. Short cards take up the
     slack through the hint's margin-top:auto. */
  .voc-flip {
    position: relative;
    height: 500px;
    transform-style: preserve-3d;
    transition: transform var(--duration-slow) var(--ease-glass);
  }
  .voc-flip-on {
    transform: rotateY(180deg);
  }

  .voc-face {
    position: absolute;
    inset: 0;
    border: 1px solid var(--glass-border);
    border-top-color: var(--glass-border-top);
    border-radius: var(--rad-catchup-large);
    background: var(--glass-popover-bg-strong);
    /* backdrop-scroll-ok: the face never scrolls and never sits over anything
       that does. It is absolutely positioned inside .voc-flip, which has a
       FIXED height, and this component declares no scroll container of its
       own — the known-word list replaces the deck rather than scrolling
       under it. The card is also the single glass surface here, not one of a
       repeated set, which is the case the rule exists to catch. */
    backdrop-filter: var(--glass-popover-filter);
    -webkit-backdrop-filter: var(--glass-popover-filter);
    box-shadow: var(--glass-shadow), var(--glass-inner);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    overflow: hidden;
    opacity: 1;
    /* 175ms is HALF of --duration-slow, i.e. the exact midpoint of the turn,
       where the card is edge-on. A near-instant cut (1ms) rather than a fade:
       a fade would show both faces at once through the translucent surface. */
    transition: opacity 1ms linear 175ms;
  }
  /* The Turkish side: the soft end of the app's warm ramp. */
  .voc-face-back {
    transform: rotateY(180deg);
    background: linear-gradient(158deg, #fce0d2, #f8bfa4);
    border-color: rgba(255, 255, 255, 0.7);
    color: var(--catchup-gray-800);
    opacity: 0;
  }
  .voc-flip-on .voc-face-front { opacity: 0; }
  .voc-flip-on .voc-face-back { opacity: 1; }

  /* Belt and braces for the hidden face. The component marks it inert,
     which is the real fix (it also drops its tab stops); this covers the
     browsers that do not implement the attribute yet. It matters most under
     prefers-reduced-motion, where there is no rotation and so
     backface-visibility never engages — the invisible back face lies flat on
     top of the front one and would otherwise swallow every tap. */
  .voc-face-back { pointer-events: none; }
  .voc-flip-on .voc-face-back { pointer-events: auto; }
  .voc-flip-on .voc-face-front { pointer-events: none; }

  /* The whole card is the flip target — a learner taps the card, exactly as
     they would turn a paper one over. A real <button> stretched over the face
     rather than a click handler on a div, so Tab reaches it and a screen
     reader announces it; the content above it is pointer-transparent and the
     few real controls opt back in with .voc-tools. */
  .voc-tap {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    border-radius: var(--rad-catchup-large);
    background: transparent;
    cursor: pointer;
  }
  .voc-tap:focus-visible {
    outline: 2px solid var(--catchup-blue);
    outline-offset: -4px;
  }

  /* The content block runs the WIDTH of the card, not a narrow column down
     the middle: the card is ~650px across and a 44ch cap left two thirds of it
     empty while the sentence wrapped three times. Each row stretches and
     centres its own text instead. */
  .voc-body {
    position: relative;
    height: 100%;
    box-sizing: border-box;
    padding: var(--space-4) var(--space-6) var(--space-5);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    text-align: center;
    pointer-events: none;
  }

  /* Top row: what kind of word on the left, the controls on the right. */
  .voc-top {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .voc-pos {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue-100);
    color: var(--catchup-blue-700);
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-bold);
  }
  .voc-face-back .voc-pos {
    background: rgba(255, 255, 255, 0.65);
    color: #b23a1c;
  }
  /* A card with no part of speech must not show an empty pill — but it still
     has to hold its place, or the tools cluster slides to the left edge. */
  .voc-pos:empty { visibility: hidden; }

  /* Listen, in a FIXED corner. Audio deliberately does
     NOT sit beside the word: a headword here runs from "a kite" to "not to
     bring food or drink into the classroom", so a control in the word's flow
     lands somewhere different on every card and the learner has to hunt for
     it. Here it is in the same place on every card, on both faces. */
  .voc-tools {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    pointer-events: auto;
  }

  /* Audio. Disabled is a STATE, not an absence: a learner must be able to see
     which words still have no recording. */
  .voc-play {
    width: 44px;
    height: 44px;
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue);
    color: var(--catchup-white);
    cursor: pointer;
    /* --catchup-blue-600 (#0f899c), as a glow under its own button. */
    box-shadow: 0 6px 16px -6px rgba(15, 137, 156, 0.55);
    transition:
      transform var(--duration-fast) var(--ease-standard),
      background var(--duration-base) var(--ease-standard);
  }
  .voc-play:hover:not(:disabled) {
    background: var(--catchup-blue-600);
    transform: scale(1.06);
  }
  .voc-play:focus-visible {
    outline: 2px solid var(--catchup-blue-700);
    outline-offset: 2px;
  }
  .voc-play-on { background: var(--catchup-blue-700); }
  .voc-play:disabled {
    background: var(--catchup-blue-100);
    color: var(--catchup-gray-300);
    box-shadow: none;
    cursor: not-allowed;
  }
  .voc-face-back .voc-play {
    background: var(--catchup-white);
    color: #b23a1c;
  }
  .voc-face-back .voc-play:disabled {
    background: rgba(255, 255, 255, 0.35);
    color: var(--catchup-gray-300);
  }


  /* The illustration. Cards with no picture simply do not render it and the
     card closes up — no placeholder box, because most of the corpus would
     then be a grey square. */
  /* The SAME size on both faces. It is one picture of one thing; shrinking it
     on the Turkish side made the card look like it had lost something in the
     turn. The top margin is deliberate too — butted against the listen
     row the picture read as part of the toolbar. */
  .voc-media {
    width: 280px;
    height: 150px;
    margin-top: var(--space-2);
    flex: none;
    border: 1px solid var(--catchup-blue-200);
    border-radius: var(--rad-catchup-large);
    background: var(--catchup-blue-50);
    overflow: hidden;
  }
  .voc-face-back .voc-media {
    border-color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.5);
  }
  .voc-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .voc-word {
    margin: 0;
    align-self: stretch;
    font-family: var(--font-sans);
    font-size: var(--text-3xl);
    font-weight: var(--font-weight-bold);
    line-height: var(--leading-tight);
    color: var(--catchup-dark-blue);
    text-wrap: balance;
  }
  /* #B23A1C is the ramp's own ink, from the same stat tile. At 26px bold
     it clears 3:1 on this ground; the smaller text below must not use it. */
  .voc-face-back .voc-word { color: #b23a1c; }

  /* The American-English note. Quiet and clearly secondary — it sits between
     the headword and the gloss, and must never compete with the word the
     learner came for. */
  .voc-variant {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    color: var(--catchup-gray-400);
  }
  .voc-variant-tag {
    padding: 1px 7px;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue-50);
    color: var(--catchup-blue-600);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.03em;
  }

  .voc-mean {
    margin: 0;
    align-self: stretch;
    font-family: var(--font-sans);
    font-size: var(--text-lg);
    line-height: var(--leading-base);
    color: var(--catchup-gray-400);
  }
  .voc-face-back .voc-mean { color: var(--catchup-gray-600); }

  /* The example sits in its own tinted block: it is a different kind of text
     from the gloss above it — a sentence to read, not a definition to scan. */
  .voc-ex {
    margin: 0;
    align-self: stretch;
    padding: var(--space-3) var(--space-5);
    border-radius: var(--rad-catchup-medium);
    background: var(--catchup-blue-50);
    font-family: var(--font-sans);
    font-size: var(--text-lg);
    line-height: var(--leading-base);
    color: var(--catchup-gray-500);
  }
  .voc-face-back .voc-ex {
    background: rgba(255, 255, 255, 0.55);
    color: var(--catchup-gray-700);
  }
  /* The focus phrase, marked inside its sentence. The backend guarantees the
     focus is a contiguous substring, which is what makes this possible.
     Weight rather than a second highlight: the sentence is already in a tinted
     box, and a tint inside a tint reads as two separate things. */
  .voc-mark {
    background: transparent;
    color: var(--catchup-blue-700);
    font-weight: var(--font-weight-bold);
  }
  .voc-face-back .voc-mark { color: var(--catchup-gray-900); }

  .voc-hint {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semi);
    color: var(--catchup-gray-300);
  }
  .voc-face-back .voc-hint { color: var(--catchup-gray-600); }














  .voc-page-btn {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--catchup-blue-200);
    border-radius: var(--rad-catchup-full);
    background: transparent;
    color: var(--catchup-blue-600);
    cursor: pointer;
    transition:
      background var(--duration-base) var(--ease-standard),
      opacity var(--duration-base) var(--ease-standard);
  }
  .voc-page-btn:hover:not(:disabled) { background: var(--catchup-blue-50); }
  .voc-page-btn:focus-visible {
    outline: 2px solid var(--catchup-blue);
    outline-offset: 2px;
  }
  .voc-page-btn:disabled {
    color: var(--catchup-light-gray);
    border-color: var(--catchup-blue-50);
    opacity: 0.6;
    cursor: not-allowed;
  }





















  .voc-empty {
    padding: var(--space-8) var(--space-4);
    text-align: center;
    font-family: var(--font-sans);
    font-size: var(--text-base);
    color: var(--catchup-gray-400);
  }

  /* A card that turns in 3D is exactly the motion a vestibular sensitivity
     reacts to, so the reduced-motion path swaps the rotation for a cross-fade
     and keeps every other affordance identical. */
  @media (prefers-reduced-motion: reduce) {
    .voc-flip,
    .voc-progress-fill,
    .voc-play,
    .voc-arrow {
      transition: none;
    }
    .voc-flip-on { transform: none; }
    .voc-face { transition: opacity var(--duration-base) var(--ease-standard); }
    .voc-face-back { transform: none; opacity: 0; }
    .voc-flip-on .voc-face-front { opacity: 0; }
    .voc-flip-on .voc-face-back { opacity: 1; }
  }

  /* Narrow screens: the card keeps the full width and the arrows drop under
     it, rather than being squeezed from both sides. */
  /* Below this the two columns do not both fit, so the list drops under the
     deck instead of squeezing the card to nothing. */
  @media (max-width: 900px) {
  }

  @media (max-width: 640px) {
    .voc-rail {
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--space-3);
    }
    .voc-stage {
      order: -1;
      flex-basis: 100%;
    }
    .voc-flip { height: 540px; }
    .voc-word { font-size: var(--text-2xl); }
    .voc-media { width: 240px; height: 136px; }
  }
`;
